import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Popover,
  Chip,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  InputLabel
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIconMui from '@mui/icons-material/Close';
import { API } from '../../../api';
import { toast } from 'react-toastify';
import DosenButton from '../../../components/ui/Buttons/DosenButton';
import useResponsiveScreenSwitch from '../../../utils/useResponsiveScreenSwitch';
import { filterHiddenMemberAttributes, unansweredAttributeListIds } from '../utils/memberAttributes';
import OnboardingDialog from '../onboarding/OnboardingDialog';
import OnboardingShell from '../onboarding/OnboardingShell';
import TeamRoleGroups from './TeamRoleGroups';
import { useAppDispatch } from '../../../redux/hooks';
import { userTeamRolesUpdate } from '../../../redux/slices/user-profile/userProfieSlice';
import {
  initialRoleAnswers,
  roleAnswerLabel,
  roleAnswersPayload,
  roleQuestion,
  roleTeams,
  unansweredAdvice,
  unansweredRoleTeams,
  viewAfterAnswers,
  type RoleAnswers,
  type TeamRolesView
} from '../utils/teamRoles';

/**
 * Why the member is being made to answer. Plain words on purpose: the member
 * is not told "audience rules", they are told what it buys them.
 */
export const MEMBER_ATTRIBUTES_HELPER =
  'Answer every list. Your team uses this to give you the right programs.';

export interface OrgMemberAttribute {
  _id: string;
  name: string;
  values: Array<{ _id: string; value: string }>;
}

export interface AttributeOption {
  id: string;
  value: string;
  groupName: string;
}

interface MemberAttributesModalProps {
  open: boolean;
  onClose: () => void;
  orgMemberAttributes: OrgMemberAttribute[];
  selectedIds: string[];
  /**
   * Org attribute LIST ids the member previously answered with "None of these
   * apply to me". Seeds the "none" ticks so re-opening the editor shows the
   * answer they already gave instead of an empty, unanswered-looking list.
   */
  declinedListIds?: string[];
  onSave: (memberAttributes: string[], declinedListIds: string[]) => void;
  /** "Welcome back" variant: the required returning-member prompt, on the onboarding card. */
  variant?: 'default' | 'welcomeBack';
  /** welcomeBack only: shown as a Back action when the editor was opened from the checklist. */
  onBack?: () => void;
  /** The profile's team roles: "Role in <team>" is asked under Team for each team picked that has roles. */
  teamRoles?: TeamRolesView | null;
}

// Figma: close = w-5 h-5 p-1.5, inner w-2.5 h-2.5 outline-2 outline-Stroke-icon-highlight outlineOffset -1
const CloseIconFigma = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      p: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box'
    }}
  >
    <Box
      sx={{
        width: 10,
        height: 10,
        outline: '2px solid var(--Stroke-icon-highlight, #fdfdfd)',
        outlineOffset: -1
      }}
    />
  </Box>
);

// Seta do dropdown — container largo para a seta 12x6 rotacionada 45° aparecer inteira
const ArrowIconFigma = () => (
  <Box
    sx={{
      width: 28,
      minWidth: 28,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}
  >
    <Box
      sx={{
        width: 12,
        height: 6,
        borderBottom: '2px solid var(--Stroke-icon-dim, rgba(134,134,134,0.8))',
        borderRight: '2px solid var(--Stroke-icon-dim, rgba(134,134,134,0.8))',
        transform: 'rotate(45deg)',
        boxSizing: 'border-box'
      }}
    />
  </Box>
);

// Checkbox: borda 1px, branca quando selected, ícone de check no meio quando selected
const CheckboxFigma = ({ selected }: { selected: boolean }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      p: '3px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box'
    }}
  >
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '4px',
        bgcolor: 'var(--Fill-Transparent-trans-normal-9/90, rgba(255,255,255,0.09))',
        border: '1px solid',
        borderColor: selected
          ? 'var(--Stroke-Form-Input-border-highlight, rgba(255,255,255,0.9))'
          : 'var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {selected && (
        <CheckIcon
          sx={{
            fontSize: 12,
            color: 'var(--Stroke-Form-Input-border-highlight, #fff)'
          }}
        />
      )}
    </Box>
  </Box>
);

const MemberAttributesModal: React.FC<MemberAttributesModalProps> = ({
  open,
  onClose,
  orgMemberAttributes,
  selectedIds,
  declinedListIds,
  onSave,
  variant = 'default',
  onBack,
  teamRoles = null
}) => {
  const isWelcomeBack = variant === 'welcomeBack';
  const { forMobile } = useResponsiveScreenSwitch();
  const [selectedIdsState, setSelectedIdsState] = useState<string[]>([]);
  const [declinedIdsState, setDeclinedIdsState] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const syncedRef = useRef(false);
  const dispatch = useAppDispatch();
  const [roleAnswers, setRoleAnswers] = useState<RoleAnswers>({});

  const visibleAttributes = useMemo(
    () => filterHiddenMemberAttributes(orgMemberAttributes),
    [orgMemberAttributes]
  );

  useEffect(() => {
    if (!open) {
      syncedRef.current = false;
      setSelectedIdsState([]);
      setDeclinedIdsState([]);
      setRoleAnswers({});
      setDropdownOpen(false);
    } else if (open && !syncedRef.current) {
      setSelectedIdsState(Array.isArray(selectedIds) ? [...selectedIds] : []);
      setDeclinedIdsState(Array.isArray(declinedListIds) ? [...declinedListIds] : []);
      setRoleAnswers(initialRoleAnswers(teamRoles));
      syncedRef.current = true;
    }
  }, [open, selectedIds, declinedListIds, teamRoles]);

  /**
   * Picking a value and declining the list are mutually exclusive answers, so
   * each toggle clears the other for that list. Without this a member could
   * save "Engineering" and "none of these" for Department at once, which the
   * requirement would accept and the audience would read as a plain value.
   */
  const toggleId = (id: string, listId: string) => {
    setSelectedIdsState((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setDeclinedIdsState((prev) => prev.filter((x) => x !== listId));
  };

  const toggleDeclined = (listId: string, valueIds: string[]) => {
    const wasDeclined = declinedIdsState.includes(listId);
    setDeclinedIdsState((prev) =>
      wasDeclined ? prev.filter((x) => x !== listId) : [...prev, listId]
    );
    if (!wasDeclined) {
      const drop = new Set(valueIds);
      setSelectedIdsState((prev) => prev.filter((x) => !drop.has(x)));
    }
  };

  /**
   * Every list in the catalog must be answered before the member can save: at
   * least one value picked, or "None of these apply to me" ticked. A list with
   * neither is exactly the state that put RDP members on an empty dashboard —
   * a program audience carries one rule per list, ANDed, so an unanswered
   * Office meant matching zero programs.
   */
  const unansweredListIds = useMemo(
    () => unansweredAttributeListIds(visibleAttributes, selectedIdsState, declinedIdsState),
    [visibleAttributes, selectedIdsState, declinedIdsState]
  );
  const allListsAnswered = unansweredListIds.length === 0;

  /** "Role in <team>" for each team picked that has roles, answered like a list (utils/teamRoles.ts). */
  const teamsWithRoles = useMemo(
    () => roleTeams(teamRoles, visibleAttributes, selectedIdsState),
    [teamRoles, visibleAttributes, selectedIdsState]
  );
  const unansweredRoles = useMemo(
    () => unansweredRoleTeams(teamsWithRoles, roleAnswers),
    [teamsWithRoles, roleAnswers]
  );
  const everythingAnswered = allListsAnswered && unansweredRoles.length === 0;
  const answerRole = (teamValueId: string, roleId: string | null | undefined) =>
    setRoleAnswers((prev) => {
      const next = { ...prev };
      if (roleId === undefined) delete next[teamValueId];
      else next[teamValueId] = roleId;
      return next;
    });

  /** Names the lists still owed, so "Save" being greyed out is never a mystery. */
  const unansweredHint = useMemo(() => {
    const lists = visibleAttributes.filter((g) => unansweredListIds.includes(g._id)).map((g) => g.name);
    const roles = teamsWithRoles.filter((t) => unansweredRoles.includes(t.teamValueId)).map(roleQuestion);
    const names = [...lists, ...roles];
    if (names.length === 0) return '';
    // A team's role question offers "My role isn't listed" where a list offers "None of these apply to me".
    return `Still to answer: ${names.join(', ')}. ${unansweredAdvice(lists.length, roles.length)}`;
  }, [unansweredListIds, visibleAttributes, teamsWithRoles, unansweredRoles]);

  const handleSave = async () => {
    if (!everythingAnswered) return;
    setSaving(true);
    setSaveError('');
    try {
      await API.updateUserMemberAttributes({
        memberAttributes: selectedIdsState,
        memberAttributesChanged: false,
        declinedListIds: declinedIdsState,
        ...(teamRoles ? { teamRoles: roleAnswersPayload(teamsWithRoles, roleAnswers) } : {})
      });
      onSave(selectedIdsState, declinedIdsState);
      if (teamRoles) dispatch(userTeamRolesUpdate(viewAfterAnswers(teamRoles, selectedIdsState, teamsWithRoles, roleAnswers)));
      onClose();
      toast.success('Attributes updated successfully');
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Your attributes could not be saved. Try again.';
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const getSelectedChips = () => {
    const chips: Array<{ id: string; label: string; listId: string; declined?: boolean; role?: string }> = [];
    visibleAttributes.forEach((g) => {
      if (declinedIdsState.includes(g._id)) {
        // The collapsed trigger has to show the decline too, or a fully
        // answered catalog reads as "nothing selected".
        chips.push({ id: `${g._id}::none`, label: `${g.name}: None`, listId: g._id, declined: true });
        return;
      }
      g.values.forEach((v) => {
        if (selectedIdsState.includes(v._id)) chips.push({ id: v._id, label: v.value, listId: g._id });
      });
      // Each team's role right after the teams themselves.
      if (g._id === teamRoles?.teamsListId) {
        teamsWithRoles.forEach((team) => {
          const answer = roleAnswerLabel(team, roleAnswers);
          if (answer) {
            chips.push({
              id: `role:${team.teamValueId}`,
              label: `${roleQuestion(team)}: ${answer}`,
              listId: g._id,
              role: team.teamValueId
            });
          }
        });
      }
    });
    return chips;
  };

  const isEmpty = getSelectedChips().length === 0;

  const getSelectedLabel = () => getSelectedChips().map((c) => c.label).join(', ');

  // Shared dropdown trigger + popover used by both variants
  const renderDropdown = () => (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box
        ref={anchorRef}
        component="button"
        type="button"
        onClick={() => setDropdownOpen((o) => !o)}
        sx={{
          width: '100%',
          pl: 1.5,
          pr: 2,
          py: 0.75,
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          background: 'rgba(255,255,255,0.07)',
          borderRadius: 1,
          outline: '1px solid',
          outlineColor: dropdownOpen
            ? 'var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))'
            : 'var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
          outlineOffset: -1,
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          '&:hover': {
            outlineColor: 'var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))'
          }
        }}
      >
        {isEmpty ? (
          <Typography
            component="span"
            sx={{
              flex: 1,
              color: 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
              fontSize: 14,
              fontFamily: 'Inter',
              lineHeight: '24px'
            }}
          />
        ) : (
          getSelectedChips().map((chip) => (
            <Chip
              key={chip.id}
              label={chip.label}
              size="small"
              onDelete={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (chip.role) {
                  answerRole(chip.role, undefined);
                } else if (chip.declined) {
                  setDeclinedIdsState((prev) => prev.filter((x) => x !== chip.listId));
                } else {
                  toggleId(chip.id, chip.listId);
                }
              }}
              deleteIcon={
                <CloseIconMui
                  sx={{
                    fontSize: '14px !important',
                    color: 'var(--Text-text-primary, #fdfdfd) !important'
                  }}
                />
              }
              sx={{
                height: 24,
                fontSize: 12,
                bgcolor: 'rgba(255,255,255,0.12)',
                color: 'var(--Text-text-primary, #fdfdfd)',
                borderRadius: '4px',
                '& .MuiChip-deleteIcon': { ml: 0.25 }
              }}
            />
          ))
        )}
        <Box sx={{ ml: 'auto', flexShrink: 0 }}>
          <ArrowIconFigma />
        </Box>
      </Box>

      <Popover
        open={dropdownOpen}
        anchorEl={anchorRef.current}
        onClose={() => setDropdownOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: anchorRef.current?.offsetWidth || 500,
            maxHeight: 384,
            mt: 1,
            padding: 0,
            // Solid surface: the popover floats over the page, not the modal,
            // so a translucent fill came out as plain black.
            background: 'var(--Fill-Modal-background, #1c1c1c)',
            borderRadius: 1,
            outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
            boxShadow: '0 12px 32px -12px rgba(0,0,0,0.8)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ overflow: 'auto', maxHeight: 360 }}>
          {visibleAttributes.map((group) => {
            const groupValueIds = group.values.map((v) => v._id);
            const groupDeclined = declinedIdsState.includes(group._id);
            const groupUnanswered = unansweredListIds.includes(group._id);
            return (
              <Box key={group._id}>
                <Box
                  sx={{
                    px: 2,
                    pt: '5px',
                    pb: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
                    fontSize: 12,
                    fontFamily: 'Inter',
                    lineHeight: '16px'
                  }}
                >
                  <span>{group.name}</span>
                  {groupUnanswered && (
                    <Box
                      component="span"
                      sx={{ color: 'var(--Text-text-warning, #f79420)', fontSize: 12, fontFamily: 'Inter' }}
                    >
                      Needs an answer
                    </Box>
                  )}
                </Box>
                {group.values.map((v) => {
                  const selected = selectedIdsState.includes(v._id);
                  return (
                    <Box
                      key={v._id}
                      component="button"
                      type="button"
                      onClick={() => toggleId(v._id, group._id)}
                      sx={{
                        width: '100%',
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                      }}
                    >
                      <CheckboxFigma selected={selected} />
                      <Typography
                        sx={{
                          color: selected
                            ? 'var(--Text-text-primary, #fdfdfd)'
                            : 'var(--Text-text-secondary, rgba(255,255,255,0.85))',
                          fontSize: 14,
                          fontFamily: 'Inter',
                          lineHeight: '20px'
                        }}
                      >
                        {v.value}
                      </Typography>
                    </Box>
                  );
                })}
                {/* The escape hatch. "Management: Managers | SLT" applies to
                    almost nobody, so requiring a VALUE from every list would be
                    unanswerable — this is the answer those members give, and it
                    is stored as a decline, not as silence. */}
                <Box
                  component="button"
                  type="button"
                  onClick={() => toggleDeclined(group._id, groupValueIds)}
                  sx={{
                    width: '100%',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                  }}
                >
                  <CheckboxFigma selected={groupDeclined} />
                  <Typography
                    sx={{
                      color: groupDeclined
                        ? 'var(--Text-text-primary, #fdfdfd)'
                        : 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
                      fontSize: 14,
                      fontFamily: 'Inter',
                      fontStyle: 'italic',
                      lineHeight: '20px'
                    }}
                  >
                    None of these apply to me
                  </Typography>
                </Box>
                {/* "Role in <team>" directly under the Teams list: only the roles of the teams picked. */}
                {group._id === teamRoles?.teamsListId && (
                  <TeamRoleGroups teams={teamsWithRoles} answers={roleAnswers} onAnswer={answerRole} look="modal" />
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );

  // "Welcome back" variant: the required prompt on the onboarding card.
  if (isWelcomeBack) {
    return (
      <OnboardingDialog
        open={open}
        onClose={() => {}}
        fullScreen={forMobile}
        data-testid="member-attributes-dialog"
        aria-labelledby="member-attributes-heading"
      >
        <OnboardingShell
          required
          heading={<span id="member-attributes-heading">Confirm your attributes</span>}
          hint="Your organization added new attributes. Add yours to continue."
        />
        <DialogContent>
          <InputLabel>My attributes</InputLabel>
          {renderDropdown()}
          <FormHelperText sx={{ mt: 1 }}>{MEMBER_ATTRIBUTES_HELPER}</FormHelperText>
          {!everythingAnswered && (
            <FormHelperText sx={{ mt: 0.5 }}>{unansweredHint}</FormHelperText>
          )}
          {saveError && <FormHelperText error sx={{ mt: 1 }}>{saveError}</FormHelperText>}
        </DialogContent>
        <DialogActions>
          {onBack && (
            <Button fullWidth variant="outlined" color="secondary" onClick={onBack}>
              Previous
            </Button>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={saving || !everythingAnswered}
          >
            {saving ? 'Saving...' : 'Save and Continue'}
          </Button>
        </DialogActions>
      </OnboardingDialog>
    );
  }

  // Default variant — compact modal with header + close button
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 550,
          maxWidth: '95vw',
          bgcolor: 'var(--Fill-Modal-background, #1c1c1c)',
          borderRadius: '8px',
          border: '1px solid var(--Stroke-Modal-border, #444)',
          outline: '1px solid var(--Stroke-Modal-border, #444)',
          outlineOffset: -1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          pl: 3,
          pr: '44px',
          py: 2.5,
          position: 'relative',
          borderBottom: '1px solid var(--Stroke-Border-border-dim, #333)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2.5
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: 'var(--Text-text-primary, #fdfdfd)',
            fontSize: 18,
            fontWeight: 700,
            fontFamily: 'Inter'
          }}
        >
          My attributes
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            width: 20,
            height: 20,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            '&:hover': { opacity: 0.85 }
          }}
        >
          <CloseIconFigma />
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          width: '100%',
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5
          }}
        >
          <Typography
            sx={{
              color: 'var(--Text-text-primary, #fdfdfd)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'Inter',
              lineHeight: '20px'
            }}
          >
            Select Attribute(s):
          </Typography>
          {renderDropdown()}
          <Typography
            sx={{
              color: 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
              fontSize: 12,
              fontFamily: 'Inter',
              lineHeight: '16px'
            }}
          >
            {MEMBER_ATTRIBUTES_HELPER}
          </Typography>
          {!everythingAnswered && (
            <Typography
              sx={{
                color: 'var(--Text-text-warning, #f79420)',
                fontSize: 12,
                fontFamily: 'Inter',
                lineHeight: '16px'
              }}
            >
              {unansweredHint}
            </Typography>
          )}
        </Box>

        <DosenButton
          sizeVariant="sm"
          tone="cta"
          onClick={handleSave}
          loading={saving}
          loadingText="Saving..."
          disabled={saving || !everythingAnswered}
          sx={{ minWidth: 192 }}
        >
          Save Changes
        </DosenButton>
      </Box>
    </Dialog>
  );
};

export default MemberAttributesModal;
